import { Link } from "react-router-dom";
import { TopAmbassadors } from "@/components/offer/TopAmbassadors";
import { ArrowRight, Brain, Bot, Plane, Rocket, School, Coins, Wrench, Trophy, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { ChampionSpotlight } from "@/components/ChampionSpotlight";
import { HeroVideo } from "@/components/HeroVideo";
import { RobotLabBanner } from "@/components/RobotLabBanner";
import { AdvancedCoursesBanner } from "@/components/AdvancedCoursesBanner";
import { FeatureCards, SolutionsRow, MotionBand } from "@/components/HomeSections";
import { IMG, TESTIMONIALS, FEATURED_IMG } from "@/lib/content";
import { TESTIMONIALS_HI } from "@/lib/content_hi";
import { useLang } from "@/context/LanguageContext";

const programs = [
  { to: "/programs/ai", icon: Brain, title: ["Artificial Intelligence", "आर्टिफिशियल इंटेलिजेंस"], desc: ["Vision, voice & generative AI built by students.", "छात्रों द्वारा बनाया विज़न, वॉइस और जनरेटिव AI।"], span: "lg:col-span-5", img: IMG.ai },
  { to: "/programs/robotics", icon: Bot, title: ["Robotics & Humanoids", "रोबोटिक्स और ह्यूमनॉइड्स"], desc: ["From line-followers to hotel service robots.", "लाइन-फॉलोअर से होटल सर्विस रोबोट तक।"], span: "lg:col-span-7", img: IMG.robot },
  { to: "/programs/uav", icon: Plane, title: ["UAV / Drone Tech", "UAV / ड्रोन टेक"], desc: ["Assemble, tune and fly mission-ready drones.", "मिशन-रेडी ड्रोन असेंबल करें, ट्यून करें और उड़ाएँ।"], span: "lg:col-span-7", img: IMG.drone },
  { to: "/programs/aerospace", icon: Rocket, title: ["Aerospace Engineering", "एयरोस्पेस इंजीनियरिंग"], desc: ["Rockets, CanSats and orbital thinking.", "रॉकेट, CanSat और अंतरिक्ष की सोच।"], span: "lg:col-span-5", img: IMG.rocket },
];

const stats = [
  { v: "120+", l: ["Partner schools", "साझेदार स्कूल"] },
  { v: "18,000+", l: ["Students trained", "प्रशिक्षित छात्र"] },
  { v: "150+", l: ["Roboshop components", "रोबोशॉप कंपोनेंट्स"] },
  { v: "₹42L+", l: ["Earned by students", "छात्रों की कमाई"] },
];

const StatsStrip = () => {
  const { L } = useLang();
  return (
    <section className="bg-[#E8F0FE] text-[#0A192F] pb-16" data-testid="home-stats">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-900/[0.07] rounded-2xl overflow-hidden border border-slate-200">
          {stats.map((s, i) => (
            <Reveal key={s.v} delay={0.1 * i} className="bg-white p-5">
              <div className="text-2xl md:text-3xl font-extrabold font-['Outfit']" data-testid={`stat-${i}`}>{s.v}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{L(...s.l)}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Programs = () => {
  const { L } = useLang();
  return (
  <section className="bg-[#F0F4F8] text-[#0A192F] py-24" data-testid="home-programs">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <span className="rs-chip">{L("Four Pillars", "चार स्तंभ")}</span>
          <h2 className="text-3xl md:text-5xl font-extrabold mt-4 max-w-xl">{L("Programs engineered for tomorrow's careers", "कल के करियर के लिए बनाए गए कार्यक्रम")}</h2>
        </div>
        <p className="text-slate-600 max-w-sm text-sm md:text-base">{L("Graded curriculum for Classes 6–8, 9–10 and 11–12 with hands-on kits and project-based assessment.", "कक्षा 6–8, 9–10 और 11–12 के लिए स्तरवार पाठ्यक्रम, हैंड्स-ऑन किट और प्रोजेक्ट-आधारित मूल्यांकन के साथ।")}</p>
      </Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {programs.map((p, i) => (
          <Reveal key={p.to} delay={0.08 * i} className={p.span}>
            <Link to={p.to} className="group relative block h-72 rounded-2xl overflow-hidden border border-slate-200 hover:border-[#00E5FF]/50 hover:shadow-[0_0_40px_rgba(0,229,255,0.15)] transition-[border-color,box-shadow] duration-500" data-testid={`program-card-${i}`}>
              <img src={p.img} alt={p.title[0]} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/80 to-white/10" />
              <div className="relative h-full p-7 flex flex-col justify-end">
                <p.icon className="w-8 h-8 text-[#007A8A] mb-3" />
                <h3 className="text-2xl font-bold">{L(...p.title)}</h3>
                <p className="text-slate-600 text-sm mt-1">{L(...p.desc)}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-[#FF2E63] font-semibold">{L("Explore curriculum", "पाठ्यक्रम देखें")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

const steps = [
  { icon: School, t: ["Learn in school", "स्कूल में सीखें"], d: ["Weekly hands-on labs in AI, Robotics, UAV & Aerospace.", "AI, रोबोटिक्स, ड्रोन और एयरोस्पेस में साप्ताहिक प्रायोगिक लैब।"] },
  { icon: Wrench, t: ["Build real products", "असली उत्पाद बनाएँ"], d: ["Students co-create IoT kits, robots & drone parts.", "छात्र मिलकर IoT किट, रोबोट और ड्रोन पुर्ज़े बनाते हैं।"] },
  { icon: Coins, t: ["Earn from sales", "बिक्री से कमाएँ"], d: ["Products sold via Roboshop share revenue with students.", "Roboshop पर बिके उत्पादों की कमाई में छात्रों का हिस्सा।"] },
  { icon: Trophy, t: ["Launch careers", "करियर शुरू करें"], d: ["Certificates, competitions & industry internships.", "प्रमाणपत्र, प्रतियोगिताएँ और इंडस्ट्री इंटर्नशिप।"] },
];

const LearnEarn = () => {
  const { L } = useLang();
  return (
  <section className="bg-[#F0F4F8] py-24" data-testid="home-learn-earn">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
      <Reveal className="lg:col-span-5">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2E63]">{L("The Learn & Earn Model", "सीखें और कमाएँ मॉडल")}</span>
        <h2 className="text-3xl md:text-5xl font-extrabold mt-4 text-[#0A192F]">{L("Education that pays back — literally.", "ऐसी शिक्षा जो सच में लौटाती है।")}</h2>
        <p className="mt-5 text-slate-600">{L("Every RoboSpark student is a maker. The best student-built products are refined, branded and sold — and students share in the earnings.", "हर RoboSpark छात्र एक मेकर है। छात्रों के बनाए सर्वश्रेष्ठ उत्पादों को निखारकर, ब्रांड करके बेचा जाता है — और कमाई में छात्रों का हिस्सा होता है।")}</p>
        <Link to="/learn-and-earn"><Button className="mt-8 rounded-full bg-white border border-[#00E5FF]/40 hover:bg-[#FF3B3B] hover:border-[#FF3B3B] text-[#0A192F] hover:text-white px-6" data-testid="home-learn-earn-btn">{L("How it works", "यह कैसे काम करता है")} <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
      </Reveal>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
        {steps.map((s, i) => (
          <Reveal key={s.t[0]} delay={0.1 * i}>
            <div className="p-6 rounded-2xl border border-slate-200 bg-[#E8F0FE] hover:bg-slate-100 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow,background-color] duration-300 h-full">
              <div className="flex items-center justify-between">
                <s.icon className="w-7 h-7 text-[#FF2E63]" />
                <span className="text-4xl font-black text-slate-700 font-['Outfit']">0{i + 1}</span>
              </div>
              <h3 className="mt-4 font-bold text-lg text-[#0A192F]">{L(...s.t)}</h3>
              <p className="text-sm text-slate-600 mt-1">{L(...s.d)}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

const featured = [
  { id: "prod-humanoid", name: ["Humanoid Robot", "ह्यूमनॉइड रोबोट"], tag: ["Flagship", "प्रमुख"] },
  { id: "prod-hotel", name: ["Hotel Service Robot", "होटल सर्विस रोबोट"], tag: ["Customised", "कस्टमाइज़्ड"] },
  { id: "prod-iot", name: ["Household AI IoT Kit", "घरेलू AI IoT किट"], tag: ["Best Seller", "बेस्ट सेलर"] },
];

const Featured = () => {
  const { L } = useLang();
  return (
  <section className="bg-[#E8F0FE] py-24 border-y border-slate-200" data-testid="home-featured">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <Reveal className="flex justify-between items-end mb-10 gap-4 flex-wrap">
        <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A192F]">{L("Built by RoboSpark. Ready to sell.", "RoboSpark द्वारा निर्मित। बिक्री के लिए तैयार।")}</h2>
        <Link to="/roboshop" className="text-[#FF2E63] font-semibold inline-flex items-center gap-1 py-2" data-testid="home-shop-link">{L("Visit Roboshop", "रोबोशॉप देखें")} <ArrowRight className="w-4 h-4" /></Link>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {featured.map((f, i) => (
          <Reveal key={f.id} delay={0.1 * i}>
            <Link to="/roboshop" className="product-tile block bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-shadow" data-testid={`featured-${f.id}`}>
              <div className="aspect-square bg-white overflow-hidden"><img src={FEATURED_IMG[f.id]} alt={f.name[0]} className="product-img w-full h-full object-contain p-4" /></div>
              <div className="p-5 border-t border-slate-200 flex justify-between items-center">
                <span className="font-bold text-[#0A192F]">{L(...f.name)}</span>
                <span className="text-[10px] uppercase tracking-widest bg-[#FF3B3B]/10 text-[#FF2E63] px-2 py-1 rounded-full font-bold">{L(...f.tag)}</span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

const Testimonials = () => {
  const { L, lang } = useLang();
  const list = lang === "hi" ? TESTIMONIALS_HI : TESTIMONIALS;
  return (
  <section className="bg-[#F0F4F8] text-[#0A192F] py-24" data-testid="home-testimonials">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <h2 className="text-3xl md:text-4xl font-extrabold mb-12">{L("Voices from our classrooms", "हमारी कक्षाओं की आवाज़ें")}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {list.map((t, i) => (
          <Reveal key={i} delay={0.1 * i}>
            <div className="p-7 rounded-2xl border border-slate-200 bg-white shadow-[0_8px_30px_rgba(10,25,47,0.06)] h-full">
              <Quote className="w-6 h-6 text-[#FF2E63]" />
              <p className="mt-4 text-slate-700 leading-relaxed">{t.text}</p>
              <div className="mt-6 text-sm"><div className="font-bold">{t.name}</div><div className="text-slate-500">{t.role}</div></div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

const CTA = () => {
  const { L } = useLang();
  return (
  <section className="bg-[#FF3B3B] text-white py-16" data-testid="home-cta">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
      <h2 className="text-3xl md:text-4xl font-extrabold max-w-2xl">{L("Bring a RoboSpark lab to your school this session.", "इसी सत्र में अपने स्कूल में RoboSpark लैब लाएँ।")}</h2>
      <div className="flex gap-3">
        <Link to="/schools"><Button size="lg" className="rounded-full bg-white hover:bg-[#E8F0FE] text-[#0A192F]" data-testid="cta-partner-btn">{L("Partner with us", "हमसे जुड़ें")}</Button></Link>
        <Link to="/contact"><Button size="lg" variant="outline" className="rounded-full bg-transparent border-white text-[#0A192F] hover:bg-slate-100 hover:text-[#FF2E63]" data-testid="cta-contact-btn">{L("Talk to us", "हमसे बात करें")}</Button></Link>
      </div>
    </div>
  </section>
  );
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <HeroVideo />
      <FeatureCards />
      <StatsStrip />
      <SolutionsRow />
      <MotionBand />
      <Programs />
      <RobotLabBanner />
      <AdvancedCoursesBanner />
      <ChampionSpotlight />
      <LearnEarn />
      <Featured />
      <Testimonials />
      <TopAmbassadors />
      <CTA />
    </div>
  );
}
